import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Search } from 'lucide-react';
import type { ProjectFile, TimberProject } from '../../types';
import { useOptionalCRM } from '../../utils/CRMContext';
import { portalHtmlPreviewHref } from '../../utils/portalAssetUrl';
import { isHtmlType, subscribeProjectFiles } from '../../utils/projectFileService';
import {
  filterHtmlCompareModels,
  filterProjectsForHtmlCompare,
  type HtmlCompareModel,
} from '../../utils/htmlCompareFilter';

export type HtmlCompareSource = 'this' | 'other';

export interface HtmlComparePickerProps {
  currentDocumentId?: string;
  currentProjectId?: string;
  thisProjectModels: HtmlCompareModel[];
  selected: HtmlCompareModel | null;
  onSelect: (model: HtmlCompareModel) => void;
}

function fileToModel(file: ProjectFile, projectName?: string): HtmlCompareModel {
  return {
    documentId: file.id || file.url,
    url: portalHtmlPreviewHref(file.url, { downloadName: file.name, sizeBytes: file.size }),
    fetchUrl: file.url,
    title: file.name,
    versionLabel: file.versionLabel || (file.versionIndex != null ? `#${file.versionIndex}` : undefined),
    projectId: file.projectId,
    projectName,
    htmlModelDescriptors: file.htmlModelDescriptors,
  };
}

function modelLabel(m: HtmlCompareModel): string {
  return [m.versionLabel, m.title].filter(Boolean).join(' · ') || '3D model';
}

export default function HtmlComparePicker({
  currentDocumentId,
  currentProjectId,
  thisProjectModels,
  selected,
  onSelect,
}: HtmlComparePickerProps) {
  const crm = useOptionalCRM();
  const projects = (crm?.timberProjects || []) as TimberProject[];
  const [source, setSource] = useState<HtmlCompareSource>(
    selected?.projectId && selected.projectId !== currentProjectId ? 'other' : 'this',
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [otherProjectId, setOtherProjectId] = useState<string | null>(
    selected?.projectId && selected.projectId !== currentProjectId ? selected.projectId : null,
  );
  const [otherFiles, setOtherFiles] = useState<ProjectFile[]>([]);
  const [otherFilesLoading, setOtherFilesLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (source !== 'other' || !otherProjectId) {
      setOtherFiles([]);
      setOtherFilesLoading(false);
      return undefined;
    }
    setOtherFilesLoading(true);
    return subscribeProjectFiles(otherProjectId, docs => {
      setOtherFiles(docs.filter(f => isHtmlType(f.mimeType, f.name)));
      setOtherFilesLoading(false);
    });
  }, [source, otherProjectId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const thisOptions = useMemo(
    () => filterHtmlCompareModels(thisProjectModels, '', currentDocumentId),
    [thisProjectModels, currentDocumentId],
  );

  const otherProjectList = useMemo(
    () => filterProjectsForHtmlCompare(
      projects.filter((p): p is TimberProject & { id: string } => !!p.id),
      otherProjectId ? '' : query,
      currentProjectId,
    ),
    [projects, query, currentProjectId, otherProjectId],
  );

  const otherProject = projects.find(p => p.id === otherProjectId);

  const otherModels = useMemo(() => {
    const projectName = otherProject?.name;
    return filterHtmlCompareModels(
      otherFiles.map(f => fileToModel(f, projectName)),
      query,
      currentDocumentId,
    );
  }, [otherFiles, otherProject?.name, query, currentDocumentId]);

  const thisSelectedId =
    selected && thisProjectModels.some(m => m.documentId === selected.documentId)
      ? selected.documentId
      : '';

  const otherTriggerLabel = selected && selected.projectId && selected.projectId !== currentProjectId
    ? `${selected.projectName || otherProject?.name || 'Other'} · ${modelLabel(selected)}`
    : otherProject
      ? `${otherProject.name} — choose a model…`
      : 'Choose a project…';

  return (
    <div className="html-compare-picker" ref={rootRef}>
      <div className="html-compare-picker-source" role="tablist" aria-label="Compare source">
        <button
          type="button"
          role="tab"
          aria-selected={source === 'this'}
          className={`html-compare-picker-tab${source === 'this' ? ' is-active' : ''}`}
          onClick={() => {
            setSource('this');
            setQuery('');
            setOpen(false);
          }}
        >
          This project
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={source === 'other'}
          className={`html-compare-picker-tab${source === 'other' ? ' is-active' : ''}`}
          onClick={() => {
            setSource('other');
            setQuery('');
            setOpen(true);
          }}
        >
          Other
        </button>
      </div>
      {source === 'this' ? (
        <label className="html-compare-picker-select-wrap">
          <span className="sr-only">This project model</span>
          <select
            className="html-compare-picker-select"
            value={thisSelectedId}
            onChange={e => {
              const match = thisProjectModels.find(m => m.documentId === e.target.value);
              if (match) onSelect(match);
            }}
          >
            <option value="" disabled>
              Choose a model…
            </option>
            {thisOptions.map(m => (
              <option key={m.documentId} value={m.documentId}>
                {modelLabel(m)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <button
          type="button"
          className="html-compare-picker-trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen(v => !v)}
          title={otherTriggerLabel}
        >
          <span>{otherTriggerLabel}</span>
        </button>
      )}
      {source === 'other' && open ? (
        <div className="html-compare-picker-popover" role="listbox" aria-label="Choose a 3D model">
          <div className="html-compare-picker-search">
            <Search size={12} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={otherProjectId ? 'Filter by file or descriptor…' : 'Search projects…'}
              autoFocus
            />
          </div>
          {!otherProjectId ? (
            otherProjectList.length === 0 ? (
              <div className="html-compare-picker-empty">
                {crm ? 'No matching projects.' : 'Project list is unavailable here.'}
              </div>
            ) : (
              otherProjectList.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className="html-compare-picker-item"
                  onClick={() => {
                    setOtherProjectId(p.id);
                    setQuery('');
                  }}
                >
                  <span className="html-compare-picker-item-title">{p.name}</span>
                  {p.clientName ? <span className="html-compare-picker-item-meta">{p.clientName}</span> : null}
                </button>
              ))
            )
          ) : (
            <>
              <button
                type="button"
                className="html-compare-picker-back"
                onClick={() => {
                  setOtherProjectId(null);
                  setQuery('');
                }}
              >
                <ChevronLeft size={12} aria-hidden />
                {otherProject?.name || 'Projects'}
              </button>
              {otherFilesLoading ? (
                <div className="html-compare-picker-empty">Loading models…</div>
              ) : otherModels.length === 0 ? (
                <div className="html-compare-picker-empty">No HTML files on this project.</div>
              ) : (
                otherModels.map(m => (
                  <button
                    key={m.documentId}
                    type="button"
                    role="option"
                    aria-selected={selected?.documentId === m.documentId}
                    className={`html-compare-picker-item${selected?.documentId === m.documentId ? ' is-selected' : ''}`}
                    onClick={() => {
                      onSelect(m);
                      setOpen(false);
                    }}
                  >
                    <span className="html-compare-picker-item-title">{m.title}</span>
                    {m.versionLabel ? <span className="html-compare-picker-item-meta">{m.versionLabel}</span> : null}
                  </button>
                ))
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
